import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../hooks/useTheme';
import UserAvatar from '../../shared/UserAvatar';
import {
  MoodBoardCardItem,
  MoodBoardCardActions,
  ImageNavigationProps,
} from './types';
import LocationMap from '../../shared/LocationMap';

interface ThumbnailCardProps {
  item: MoodBoardCardItem;
  actions: MoodBoardCardActions;
  imageNavigation: ImageNavigationProps;
  animatedStyle?: {
    opacity?: Animated.Value;
    transform?: Array<{ scale?: Animated.Value }>;
  };
}

const ThumbnailCard: React.FC<ThumbnailCardProps> = ({
  item,
  actions,
  imageNavigation,
  animatedStyle,
}) => {
  const { colors } = useTheme();
  const currentImage =
    item.images && item.images.length > 0
      ? item.images[imageNavigation.currentIndex]
      : null;
  const hasMultipleImages = item.images && item.images.length > 1;

  const handlePress = () => {
    if (actions.onPress) {
      actions.onPress(item.id);
    }
  };

  const handleLike = (e?: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault?.();
    }
    if (actions.onLike) {
      actions.onLike(item.id);
    }
  };

  const handleShare = (e?: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault?.();
    }
    if (actions.onShare) {
      actions.onShare(item);
    }
  };

  const handleSave = (e?: any) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault?.();
    }
    if (actions.onSave) {
      actions.onSave(item.id);
    }
  };

  const cardContent = (
    <View style={styles.card}>
      <TouchableOpacity
        style={styles.touchableWrapper}
        onPress={handlePress}
        activeOpacity={0.95}
      >
        {/* Image */}
        {currentImage ? (
          <View style={styles.imageContainer}>
            <Image
              source={{ uri: currentImage }}
              style={styles.image}
              resizeMode="cover"
            />
            {hasMultipleImages && (
              <>
                {/* Previous Button */}
                <TouchableOpacity
                  style={styles.navButton}
                  onPress={(e) => {
                    e.stopPropagation();
                    imageNavigation.onPrev();
                  }}
                  activeOpacity={0.7}
                >
                  <Ionicons name="chevron-back" size={18} color="white" />
                </TouchableOpacity>
                {/* Next Button */}
                <TouchableOpacity
                  style={[styles.navButton, styles.navButtonRight]}
                  onPress={(e) => {
                    e.stopPropagation();
                    imageNavigation.onNext();
                  }}
                  activeOpacity={0.7}
                >
                  <Ionicons name="chevron-forward" size={18} color="white" />
                </TouchableOpacity>
                {/* Image Count Badge */}
                {imageNavigation.showCount && (
                  <View style={styles.imageCountBadge}>
                    <Ionicons name="images" size={12} color="white" />
                    <Text style={styles.imageCountText}>
                      {imageNavigation.currentIndex + 1}/
                      {imageNavigation.totalImages}
                    </Text>
                  </View>
                )}
              </>
            )}
            {item.isLiked && (
              <View style={styles.likedBadge}>
                <Ionicons name="heart" size={14} color="#FF6B6B" />
              </View>
            )}
          </View>
        ) : (
          <View style={[styles.imageContainer, styles.emptyImage]}>
            <Ionicons name="image-outline" size={32} color={colors.secondary} />
          </View>
        )}

        {/* Bottom overlay with all details */}
        <View style={styles.overlay}>
          {/* Main Content - Flexible */}
          <View style={styles.mainContent}>
            {/* Header with User Info and Privacy Badge */}
            <View style={styles.header}>
              <View style={styles.userInfo}>
                <UserAvatar
                  size={20}
                  imageUrl={item.userAvatar}
                  name={item.username}
                  email={item.userEmail}
                  showActiveStatus={true}
                  isActive={true}
                />
                <View style={styles.userDetails}>
                  <Text style={[styles.username, { color: colors.text }]}>
                    {item.username}
                  </Text>
                  <Text style={[styles.timestamp, { color: colors.secondary }]}>
                    {item.timestamp}
                  </Text>
                </View>
              </View>
              {/* Privacy Badge */}
              <View
                style={[
                  styles.privacyBadge,
                  {
                    backgroundColor: item.is_public
                      ? 'rgba(34, 197, 94, 0.1)'
                      : 'rgba(107, 114, 128, 0.1)',
                  },
                ]}
              >
                <Ionicons
                  name={item.is_public ? 'globe-outline' : 'lock-closed'}
                  size={10}
                  color={item.is_public ? '#22C55E' : '#6B7280'}
                />
              </View>
            </View>

            {/* Title */}
            <Text
              style={[styles.title, { color: colors.text }]}
              numberOfLines={1}
            >
              {item.title && item.title.length > 20
                ? `${item.title.substring(0, 20)}...`
                : item.title}
            </Text>

            {/* Location */}
            {item.location && (
              <View style={styles.locationSection}>
                <View style={styles.locationRow}>
                  <Ionicons
                    name="location-outline"
                    size={10}
                    color={colors.secondary}
                  />
                  <Text
                    style={[styles.locationText, { color: colors.secondary }]}
                    numberOfLines={1}
                  >
                    {item.location.length > 25
                      ? `${item.location.substring(0, 25)}...`
                      : item.location}
                  </Text>
                </View>
              </View>
            )}
          </View>

          {/* Fixed Bottom Section - Colors and Actions */}
          <View style={styles.bottomSection}>
            {/* Color Palette - Fixed at bottom */}
            {item.colors && item.colors.length > 0 && (
              <View style={styles.colorsSection}>
                <View style={styles.colorPalette}>
                  {item.colors
                    .slice(0, 5)
                    .map((color: string, colorIndex: number) => (
                      <View
                        key={colorIndex}
                        style={[styles.colorCircle, { backgroundColor: color }]}
                      />
                    ))}
                </View>
              </View>
            )}

            {/* Stats Section - Above action buttons */}
            <View style={styles.statsSection}>
              <Text style={[styles.statsText, { color: colors.secondary }]}>
                {item.likes || 0} likes • {item.shares || 0} shares •{' '}
                {item.saved || 0} saved
              </Text>
            </View>

            {/* Actions Row - Fixed at bottom */}
            <View style={styles.actionsRow}>
              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleLike}
                activeOpacity={0.7}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name={item.isLiked ? 'heart' : 'heart-outline'}
                  size={18}
                  color={item.isLiked ? '#FF6B6B' : colors.secondary}
                />
                <Text
                  style={[
                    styles.actionText,
                    { color: item.isLiked ? '#FF6B6B' : colors.secondary },
                  ]}
                >
                  Like
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleShare}
                activeOpacity={0.7}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name="share-outline"
                  size={18}
                  color={colors.secondary}
                />
                <Text style={[styles.actionText, { color: colors.secondary }]}>
                  Share
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.actionButton}
                onPress={handleSave}
                activeOpacity={0.7}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons
                  name={item.isSaved ? 'bookmark' : 'bookmark-outline'}
                  size={18}
                  color={item.isSaved ? '#FFA500' : colors.secondary}
                />
                <Text
                  style={[
                    styles.actionText,
                    { color: item.isSaved ? '#FFA500' : colors.secondary },
                  ]}
                >
                  {item.isSaved ? 'Saved' : 'Save'}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );

  if (animatedStyle) {
    return (
      <Animated.View style={[styles.container, animatedStyle as any]}>
        {cardContent}
      </Animated.View>
    );
  }

  return <View style={styles.container}>{cardContent}</View>;
};

const styles = StyleSheet.create({
  container: {
    ...(Platform.OS === 'web'
      ? { width: 380 }
      : { width: '48%', marginHorizontal: '1%', minWidth: 180 }),
    marginBottom: Platform.OS === 'web' ? 16 : 20,
    marginTop: 0,
    alignSelf: 'flex-start',
  },
  card: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    width: Platform.OS === 'web' ? 380 : undefined,
    minHeight: Platform.OS === 'web' ? 600 : 550,
    aspectRatio: Platform.OS === 'web' ? 0.75 : 0.8,
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  touchableWrapper: {
    flex: 1,
    padding: 0,
    margin: 0,
  },
  imageContainer: {
    width: '100%',
    height: Platform.OS === 'web' ? 380 : 320,
    borderRadius: 0,
    overflow: 'visible',
    position: 'relative',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000000',
    alignSelf: 'center',
    margin: 0,
    padding: 0,
  },
  image: {
    width: '100%',
    height: '100%',
    flexShrink: 0,
    alignSelf: 'center',
  },
  emptyImage: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  imageCountBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.6)',
    paddingHorizontal: 6,
    paddingVertical: 4,
    borderRadius: 12,
    gap: 4,
  },
  imageCountText: {
    color: 'white',
    fontSize: Platform.OS === 'web' ? 12 : 13,
    fontWeight: '600',
  },
  likedBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    backgroundColor: 'rgba(255,255,255,0.9)',
    borderRadius: 12,
    padding: 4,
  },
  navButton: {
    position: 'absolute',
    left: 8,
    top: '50%',
    marginTop: -18,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  navButtonRight: {
    left: 'auto',
    right: 8,
  },
  overlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(255,255,255,0.98)',
    padding: 14,
    height: Platform.OS === 'web' ? 220 : 200,
    flexDirection: 'column',
    justifyContent: 'space-between',
    margin: 0,
    marginTop: 0,
    marginBottom: 0,
  },
  mainContent: {
    flex: 1,
    minHeight: 0,
  },
  bottomSection: {
    marginTop: 'auto',
    flexShrink: 0,
  },
  colorsSection: {
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.05)',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: 6,
  },
  userDetails: {
    flex: 1,
  },
  username: {
    fontSize: Platform.OS === 'web' ? 13 : 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  timestamp: {
    fontSize: Platform.OS === 'web' ? 11 : 12,
    opacity: 0.7,
  },
  privacyBadge: {
    padding: 4,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: Platform.OS === 'web' ? 14 : 15,
    fontWeight: '600',
    marginBottom: 6,
  },
  locationSection: {
    marginTop: 4,
    marginBottom: 4,
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  locationText: {
    fontSize: Platform.OS === 'web' ? 11 : 12,
    opacity: 0.7,
    flex: 1,
  },
  colorPalette: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  colorCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  statsSection: {
    marginBottom: 8,
    paddingBottom: 8,
  },
  statsText: {
    fontSize: Platform.OS === 'web' ? 12 : 13,
    fontWeight: '500',
    opacity: 0.8,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
    paddingTop: 8,
    paddingBottom: 0,
    flexShrink: 0,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 4,
  },
  actionText: {
    fontSize: Platform.OS === 'web' ? 12 : 13,
    fontWeight: '500',
  },
});

export default ThumbnailCard;
